import React, { useState, useEffect, useRef, useMemo } from 'react';
import { api, BASE_URL } from '../api';
import ActionMenu from '../components/ActionMenu';
import { useNotification } from '../components/NotificationContext';

interface DocItem {
    name: string;
    is_dir: boolean;
    size: number;
    modified: number;
}

const SafeMarkdown: React.FC<{ content: string }> = ({ content }) => {
    const lines = content.split('\n');
    return (
        <>
            {lines.map((line, idx) => {
                const trimmed = line.trim();

                // Headers
                if (line.startsWith('### ')) return <h3 key={idx}>{line.substring(4)}</h3>;
                if (line.startsWith('## ')) return <h2 key={idx}>{line.substring(3)}</h2>;
                if (line.startsWith('# ')) return <h1 key={idx}>{line.substring(2)}</h1>;

                // Horizontal Rule
                if (trimmed === '---' || trimmed === '***') return <hr key={idx} />;

                // Lists
                if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
                    return <li key={idx} style={{ marginLeft: '20px', marginBottom: '8px' }}>{line.trim().substring(2)}</li>;
                }

                // Numbers
                if (/^\d+\.\s/.test(trimmed)) {
                    return <li key={idx} style={{ marginLeft: '20px', marginBottom: '8px' }}>{line.trim().replace(/^\d+\.\s/, '')}</li>;
                }

                // Empty line
                if (trimmed === '') return <div key={idx} style={{ height: '12px' }} />;

                // Paragraph with some basic inline formatting
                const formatted = line.split(/(\*\*.*?\*\*|\*.*?\*|`.*?`)/).map((part, i) => {
                    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
                    if (part.startsWith('*') && part.endsWith('*')) return <em key={i}>{part.slice(1, -1)}</em>;
                    if (part.startsWith('`') && part.endsWith('`')) return <code key={i} style={{ background: '#f0f0f0', padding: '2px 4px', borderRadius: '4px' }}>{part.slice(1, -1)}</code>;
                    return part;
                });

                return <p key={idx} style={{ marginBottom: '16px' }}>{formatted}</p>;
            })}
        </>
    );
};

const Documentation: React.FC = () => {
    const { notify } = useNotification();
    const [items, setItems] = useState<DocItem[]>([]);
    const [currentPath, setCurrentPath] = useState('');
    const [sortConfig, setSortConfig] = useState<{ key: 'name' | 'modified', direction: 'asc' | 'desc' } | null>(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showMkdirModal, setShowMkdirModal] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [uploadFiles, setUploadFiles] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const [dragOverItem, setDragOverItem] = useState<string | null>(null);
    const [dragOverBreadcrumb, setDragOverBreadcrumb] = useState<number | 'root' | null>(null);
    const [viewFile, setViewFile] = useState<{
        name: string,
        content?: string,
        type: 'text' | 'pdf' | 'excel',
        extension: string,
        sheets?: { [key: string]: { headers: string[], rows: any[][] } },
        rawUrl?: string
    } | null>(null);
    const [activeSheet, setActiveSheet] = useState<string | null>(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        fetchDocuments();
    }, [currentPath]);

    const fetchDocuments = async () => {
        try {
            const data = await api.get(`/user/documents?path=${encodeURIComponent(currentPath)}`);
            setItems(data.documents);
        } catch (error: any) {
            console.error("Failed to fetch documents", error);
            notify("Errore nel caricamento dei documenti", "error");
        }
    };

    const handleDownload = (name: string) => {
        const fullPath = currentPath ? `${currentPath}/${name}` : name;
        const token = localStorage.getItem('access_token');
        window.open(`${BASE_URL}/user/document/${encodeURIComponent(fullPath)}?jwt=${token}`, '_blank');
    };

    const handleView = async (name: string) => {
        const fullPath = currentPath ? `${currentPath}/${name}` : name;
        try {
            const data = await api.get(`/user/document/${encodeURIComponent(fullPath)}/view`);

            let viewData: any = {
                name: data.filename,
                type: data.type,
                extension: data.extension
            };

            if (data.type === 'text') {
                let content = data.content;
                if (data.extension === 'json') {
                    try {
                        const parsed = JSON.parse(content);
                        content = JSON.stringify(parsed, null, 2);
                    } catch (e) { }
                }
                viewData.content = content;
            } else if (data.type === 'excel') {
                viewData.sheets = data.sheets;
                const sheetNames = Object.keys(data.sheets);
                if (sheetNames.length > 0) {
                    setActiveSheet(sheetNames[0]);
                }
            } else if (data.type === 'pdf') {
                const token = localStorage.getItem('access_token');
                viewData.rawUrl = `${BASE_URL}/user/document/${encodeURIComponent(fullPath)}/raw?jwt=${token}`;
            }

            setViewFile(viewData);
            setShowViewModal(true);
        } catch (error: any) {
            notify(error.message || 'Errore durante la lettura del file', 'error');
        }
    };

    const handleDelete = async (item: DocItem) => {
        const fullPath = currentPath ? `${currentPath}/${item.name}` : item.name;
        const typeStr = item.is_dir ? 'la cartella' : 'il file';

        if (window.confirm(`Sei sicuro di voler rimuovere ${typeStr} "${item.name}"?`)) {
            try {
                await api.delete(`/user/document/${encodeURIComponent(fullPath)}`);
                fetchDocuments();
            } catch (error: any) {
                notify(error.message || 'Errore durante l\'eliminazione', 'error');
            }
        }
    };

    const handleUpload = async () => {
        if (uploadFiles.length === 0) return;
        setUploading(true);
        const formData = new FormData();
        uploadFiles.forEach(file => formData.append('files', file));
        formData.append('path', currentPath);

        try {
            await api.post('/user/documents/upload', formData);
            setUploadFiles([]);
            setShowUploadModal(false);
            fetchDocuments();
        } catch (error: any) {
            notify(error.message || 'Errore durante l\'upload', 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return;
        try {
            await api.post('/user/documents/mkdir', { path: currentPath, name: newFolderName.trim() });
            setNewFolderName('');
            setShowMkdirModal(false);
            fetchDocuments();
        } catch (error: any) {
            notify(error.message || 'Errore creazione cartella', 'error');
        }
    };

    const handleMove = async (sourceName: string, destDir: string) => {
        const sourcePath = currentPath ? `${currentPath}/${sourceName}` : sourceName;
        try {
            await api.post('/user/documents/move', { source: sourcePath, destination_dir: destDir });
            notify("Elemento spostato con successo", "success");
            fetchDocuments();
        } catch (error: any) {
            notify(error.message || 'Errore durante lo spostamento', 'error');
        }
    };

    const handleMrcTransform = async (name: string) => {
        const fullPath = currentPath ? `${currentPath}/${name}` : name;
        try {
            const res = await api.post('/user/document/mrc-transform', { filename: fullPath });
            notify(res.message, 'success');
            fetchDocuments();
        } catch (error: any) {
            notify(error.message || 'Errore durante la trasformazione MRC', 'error');
        }
    };

    const onDragStart = (e: React.DragEvent, name: string) => {
        e.dataTransfer.setData("itemName", name);
    };

    const onDragOver = (e: React.DragEvent, id: string | number | 'root') => {
        e.preventDefault();
        if (typeof id === 'string' && id !== e.dataTransfer.getData("itemName")) {
            setDragOverItem(id);
        } else if (typeof id === 'number' || id === 'root') {
            setDragOverBreadcrumb(id);
        }
    };

    const onDragLeave = () => {
        setDragOverItem(null);
        setDragOverBreadcrumb(null);
    };

    const onDrop = (e: React.DragEvent, destRelPath: string) => {
        e.preventDefault();
        const itemName = e.dataTransfer.getData("itemName");
        setDragOverItem(null);
        setDragOverBreadcrumb(null);

        if (!itemName) return;

        const fullDestPath = destRelPath; // destRelPath should already be relative to root

        // Prevent dropping onto itself
        if (currentPath === fullDestPath && itemName) {
            // Already there
            return;
        }

        handleMove(itemName, fullDestPath);
    };

    const formatSize = (bytes: number) => {
        if (bytes === 0) return '-';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const sortedItems = useMemo(() => {
        let sortableItems = [...items];
        // Folders first
        sortableItems.sort((a, b) => {
            if (a.is_dir && !b.is_dir) return -1;
            if (!a.is_dir && b.is_dir) return 1;

            if (sortConfig !== null) {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
            }
            return a.name.localeCompare(b.name);
        });
        return sortableItems;
    }, [items, sortConfig]);

    const navigateTo = (name: string) => {
        setCurrentPath(prev => prev ? `${prev}/${name}` : name);
    };

    const navigateBack = () => {
        const parts = currentPath.split('/');
        parts.pop();
        setCurrentPath(parts.join('/'));
    };

    const navigateToBreadcrumb = (index: number) => {
        const parts = currentPath.split('/');
        setCurrentPath(parts.slice(0, index + 1).join('/'));
    };

    const getBreadcrumbPath = (index: number) => {
        const parts = currentPath.split('/');
        return parts.slice(0, index + 1).join('/');
    };

    const requestSort = (key: 'name' | 'modified') => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIndicator = (key: 'name' | 'modified') => {
        if (!sortConfig || sortConfig.key !== key) return '↕';
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    return (
        <div className="container" style={{ marginTop: '20px' }}>
            <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <h2 style={{ margin: 0 }}>Documentazione</h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', fontSize: '0.9rem', color: '#666' }}>
                            <span
                                style={{
                                    cursor: 'pointer',
                                    color: (currentPath || dragOverBreadcrumb === 'root') ? 'var(--primary-color)' : 'inherit',
                                    fontWeight: !currentPath ? 'bold' : 'normal',
                                    background: dragOverBreadcrumb === 'root' ? '#e3f2fd' : 'transparent',
                                    padding: '2px 4px',
                                    borderRadius: '4px'
                                }}
                                onClick={() => setCurrentPath('')}
                                onDragOver={(e) => onDragOver(e, 'root')}
                                onDragLeave={onDragLeave}
                                onDrop={(e) => onDrop(e, '')}
                            >
                                <i className="fas fa-home"></i> Root
                            </span>
                            {currentPath.split('/').filter(Boolean).map((part, idx, arr) => (
                                <React.Fragment key={idx}>
                                    <span>/</span>
                                    <span
                                        style={{
                                            cursor: idx < arr.length - 1 ? 'pointer' : 'default',
                                            color: (idx < arr.length - 1 || dragOverBreadcrumb === idx) ? 'var(--primary-color)' : 'inherit',
                                            fontWeight: idx === arr.length - 1 ? 'bold' : 'normal',
                                            background: dragOverBreadcrumb === idx ? '#e3f2fd' : 'transparent',
                                            padding: '2px 4px',
                                            borderRadius: '4px'
                                        }}
                                        onClick={() => idx < arr.length - 1 && navigateToBreadcrumb(idx)}
                                        onDragOver={(e) => idx < arr.length - 1 && onDragOver(e, idx)}
                                        onDragLeave={onDragLeave}
                                        onDrop={(e) => idx < arr.length - 1 && onDrop(e, getBreadcrumbPath(idx))}
                                    >
                                        {part}
                                    </span>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="btn btn-secondary" onClick={() => setShowMkdirModal(true)}>
                            NUOVA CARTELLA
                        </button>
                        <button className="btn btn-primary" onClick={() => setShowUploadModal(true)}>
                            CARICA DOCUMENTI
                        </button>
                    </div>
                </div>

                {items.length === 0 && !currentPath ? (
                    <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>Nessun documento presente.</p>
                ) : (
                    <div className="table-responsive">
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                                    <th onClick={() => requestSort('name')} style={{ cursor: 'pointer', padding: '12px', userSelect: 'none' }}>
                                        Nome {getSortIndicator('name')}
                                    </th>
                                    <th style={{ padding: '12px' }}>Dimensione</th>
                                    <th onClick={() => requestSort('modified')} style={{ cursor: 'pointer', padding: '12px', userSelect: 'none' }}>
                                        Ultima Modifica {getSortIndicator('modified')}
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'right' }}>Azioni</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentPath && (
                                    <tr
                                        onClick={navigateBack}
                                        style={{ cursor: 'pointer', borderBottom: '1px solid #eee' }}
                                        className="hover-row"
                                    >
                                        <td colSpan={4} style={{ padding: '12px' }}>
                                            <i className="fas fa-level-up-alt" style={{ marginRight: '8px', color: '#ffa000', transform: 'rotate(-90deg)' }}></i>
                                            <strong>.. (Indietro)</strong>
                                        </td>
                                    </tr>
                                )}
                                {sortedItems.map((item, idx) => {
                                    const isBeingDraggedOver = dragOverItem === item.name;
                                    return (
                                        <tr
                                            key={idx}
                                            style={{
                                                borderBottom: '1px solid #eee',
                                                background: (item.is_dir && isBeingDraggedOver) ? '#e3f2fd' : 'transparent'
                                            }}
                                            className="hover-row"
                                            draggable
                                            onDragStart={(e) => onDragStart(e, item.name)}
                                            onDragOver={(e) => item.is_dir && onDragOver(e, item.name)}
                                            onDragLeave={onDragLeave}
                                            onDrop={(e) => item.is_dir && onDrop(e, currentPath ? `${currentPath}/${item.name}` : item.name)}
                                        >
                                            <td
                                                onClick={() => item.is_dir && navigateTo(item.name)}
                                                style={{ cursor: item.is_dir ? 'pointer' : 'default', padding: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}
                                            >
                                                <i
                                                    className={item.is_dir ? "fas fa-folder" : "far fa-file"}
                                                    style={{ color: item.is_dir ? '#ffa000' : '#777', fontSize: '1.1rem' }}
                                                ></i>
                                                <span style={{ fontWeight: item.is_dir ? '500' : 'normal' }}>{item.name}</span>
                                            </td>
                                            <td style={{ padding: '12px' }}>{item.is_dir ? '-' : formatSize(item.size)}</td>
                                            <td style={{ padding: '12px' }}>{new Date(item.modified * 1000).toLocaleString()}</td>
                                            <td style={{ padding: '12px', textAlign: 'right' }}>
                                                <ActionMenu actions={[
                                                    { label: 'Download', icon: 'fas fa-download', onClick: () => handleDownload(item.name) },
                                                    ...(!item.is_dir ? [
                                                        ...(item.name.toLowerCase().match(/\.(xml|json|txt|text|pdf|xlsx|xls|md)$/)
                                                            ? [{ label: 'Visualizza', icon: 'fas fa-eye', onClick: () => handleView(item.name) }]
                                                            : []),
                                                        ...(item.name.toLowerCase().endsWith('.mrc')
                                                            ? [{ label: 'Trasforma MRC', icon: 'fas fa-file-export', onClick: () => handleMrcTransform(item.name) }]
                                                            : [])
                                                    ] : []),
                                                    { label: 'Rimuovi', icon: 'fas fa-trash-can', onClick: () => handleDelete(item), color: 'var(--error-color)' },
                                                ]} />
                                            </td>
                                        </tr>
                                    );
                                })}
                                {items.length === 0 && currentPath && (
                                    <tr>
                                        <td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                            Questa cartella è vuota.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showMkdirModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '400px' }}>
                        <h2>Nuova Cartella</h2>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Nome cartella"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            autoFocus
                            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                            style={{ width: '100%', marginBottom: '20px', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                        />
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button className="btn btn-text" onClick={() => setShowMkdirModal(false)}>Annulla</button>
                            <button className="btn btn-primary" onClick={handleCreateFolder} disabled={!newFolderName.trim()}>
                                CREA
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showUploadModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '500px' }}>
                        <h2>Carica Documenti</h2>
                        <p style={{ marginBottom: '10px', fontSize: '0.9rem', color: '#666' }}>
                            I file verranno caricati in: <strong>{currentPath || 'Root'}</strong>
                        </p>

                        <input
                            type="file"
                            multiple
                            onChange={(e) => setUploadFiles(Array.from(e.target.files || []))}
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                        />

                        <div
                            style={{
                                border: '2px dashed #ccc',
                                padding: '30px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                borderRadius: '8px',
                                marginBottom: '20px'
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {uploadFiles.length === 0 ? 'Trascina qui i file o clicca per selezionare' : `${uploadFiles.length} file selezionati`}
                        </div>

                        {uploadFiles.length > 0 && (
                            <ul style={{ maxHeight: '150px', overflowY: 'auto', marginBottom: '20px', fontSize: '0.85rem' }}>
                                {uploadFiles.map((f, i) => <li key={i}>{f.name}</li>)}
                            </ul>
                        )}

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button className="btn btn-text" onClick={() => setShowUploadModal(false)} disabled={uploading}>Annulla</button>
                            <button
                                className="btn btn-primary"
                                onClick={handleUpload}
                                disabled={uploading || uploadFiles.length === 0}
                            >
                                {uploading ? 'Caricamento...' : 'CARICA'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showViewModal && viewFile && (
                <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
                    <div className="modal-content" style={{ maxWidth: '90%', width: '1200px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: '0', overflow: 'hidden', borderRadius: '12px' }} onClick={e => e.stopPropagation()}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '16px 24px',
                            background: 'var(--primary-color)',
                            color: 'white'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <i className={
                                    viewFile.extension === 'xml' ? "fas fa-code" :
                                        viewFile.extension === 'json' ? "fas fa-braces" :
                                            viewFile.extension === 'pdf' ? "fas fa-file-pdf" :
                                                (viewFile.extension === 'xlsx' || viewFile.extension === 'xls') ? "fas fa-file-excel" :
                                                    viewFile.extension === 'md' ? "fab fa-markdown" :
                                                        "fas fa-file-lines"
                                }></i>
                                {viewFile.name}
                            </h2>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                {viewFile.type === 'text' && (
                                    <button
                                        className="btn btn-text"
                                        style={{ color: 'white', padding: '4px 8px' }}
                                        title="Copia negli appunti"
                                        onClick={() => {
                                            navigator.clipboard.writeText(viewFile.content || '');
                                            notify("Contenuto copiato negli appunti", "success");
                                        }}
                                    >
                                        <i className="fas fa-copy"></i>
                                    </button>
                                )}
                                <button className="btn btn-text" style={{ color: 'white', padding: '4px 8px' }} onClick={() => setShowViewModal(false)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>

                        {viewFile.type === 'excel' && viewFile.sheets && (
                            <div style={{ display: 'flex', gap: '10px', padding: '10px 20px', background: '#eceff1', borderBottom: '1px solid #cfd8dc', overflowX: 'auto' }}>
                                {Object.keys(viewFile.sheets).map(sheet => (
                                    <button
                                        key={sheet}
                                        onClick={() => setActiveSheet(sheet)}
                                        style={{
                                            padding: '6px 12px',
                                            borderRadius: '4px',
                                            border: 'none',
                                            background: activeSheet === sheet ? 'var(--primary-color)' : 'white',
                                            color: activeSheet === sheet ? 'white' : '#555',
                                            cursor: 'pointer',
                                            fontSize: '0.85rem',
                                            fontWeight: '500',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        {sheet}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div
                            style={{
                                flex: 1,
                                overflow: 'auto',
                                background: viewFile.type === 'text' ? (viewFile.extension === 'md' ? 'white' : '#1e1e1e') : 'white',
                                padding: (viewFile.type === 'excel' || viewFile.extension === 'md') ? '0' : '20px',
                                whiteSpace: viewFile.type === 'text' && viewFile.extension !== 'md' ? 'pre' : 'normal',
                                fontFamily: viewFile.type === 'text' && viewFile.extension !== 'md' ? '"Fira Code", "Source Code Pro", "Courier New", Courier, monospace' : 'inherit',
                                fontSize: '0.85rem',
                                color: viewFile.type === 'text' ? (viewFile.extension === 'md' ? '#333' : '#d4d4d4') : 'inherit',
                                lineHeight: '1.5'
                            }}
                        >
                            {viewFile.type === 'text' && (
                                viewFile.extension === 'md' ? (
                                    <div className="markdown-body" style={{ padding: '30px', maxWidth: '800px', margin: '0 auto' }}>
                                        <SafeMarkdown content={viewFile.content || ''} />
                                    </div>
                                ) : viewFile.content
                            )}

                            {viewFile.type === 'pdf' && (
                                <iframe
                                    src={(viewFile as any).rawUrl}
                                    style={{ width: '100%', height: '100%', border: 'none' }}
                                    title={viewFile.name}
                                />
                            )}

                            {viewFile.type === 'excel' && activeSheet && viewFile.sheets?.[activeSheet] && (
                                <div style={{ overflow: 'auto', height: '100%' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                        <thead>
                                            <tr style={{ background: '#f5f5f5', position: 'sticky', top: 0 }}>
                                                {viewFile.sheets[activeSheet].headers.map((h, i) => (
                                                    <th key={i} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', fontWeight: 'bold' }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {viewFile.sheets[activeSheet].rows.map((row, ri) => (
                                                <tr key={ri}>
                                                    {row.map((cell, ci) => (
                                                        <td key={ci} style={{ border: '1px solid #ddd', padding: '6px 8px' }}>
                                                            {cell === null ? '' : String(cell)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 24px', background: '#f5f5f5', borderTop: '1px solid #ddd' }}>
                            <button className="btn btn-secondary" onClick={() => setShowViewModal(false)}>CHIUDI</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Documentation;
